import { useState, useEffect, useCallback } from 'react';
import { Database, LogOut } from 'lucide-react';
import type { UserInfo, FabricData } from '../types';
import { fetchStatus, refreshAll, refreshDatabase } from '../services/api';
import { SummaryCards } from './SummaryCards';
import { DatabaseTable } from './DatabaseTable';

interface DashboardProps {
  user: UserInfo | null;
  onLogout: () => void;
}

export function Dashboard({ user, onLogout }: DashboardProps) {
  const [fabricData, setFabricData] = useState<FabricData | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshingDatabases, setRefreshingDatabases] = useState<Set<string>>(
    new Set()
  );

  const displayName = user?.clientPrincipal?.userDetails ?? 'User';

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchStatus();
      setFabricData(data);
    } catch (error) {
      console.error('Failed to fetch status:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRefreshAll = useCallback(async () => {
    setLoading(true);
    try {
      const data = await refreshAll();
      setFabricData(data);
    } catch (error) {
      console.error('Failed to refresh all:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleRefreshDatabase = useCallback(async (dbId: string) => {
    setRefreshingDatabases((prev) => {
      const next = new Set(prev);
      next.add(dbId);
      return next;
    });
    try {
      const data = await refreshDatabase(dbId);
      setFabricData(data);
    } catch (error) {
      console.error(`Failed to refresh database ${dbId}:`, error);
    } finally {
      setRefreshingDatabases((prev) => {
        const next = new Set(prev);
        next.delete(dbId);
        return next;
      });
    }
  }, []);

  return (
    <div className="min-h-screen bg-gray-950 text-gray-100">
      {/* Header */}
      <header className="sticky top-0 z-30 backdrop-blur-lg bg-gray-950/80 border-b border-gray-800/60">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-blue-600/20 border border-blue-500/20 flex items-center justify-center">
              <Database className="w-5 h-5 text-blue-400" />
            </div>
            <h1 className="text-xl font-bold text-gray-100 tracking-tight">
              Fabric Monitor
            </h1>
          </div>

          <div className="flex items-center gap-4">
            <span className="hidden sm:inline text-sm text-gray-400">
              {displayName}
            </span>
            <button
              onClick={onLogout}
              className="flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg text-gray-400 hover:text-gray-200 hover:bg-gray-800/60 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-gray-600"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        <SummaryCards data={fabricData} />
        <DatabaseTable
          data={fabricData}
          onRefreshAll={handleRefreshAll}
          onRefreshDatabase={handleRefreshDatabase}
          refreshingDatabases={refreshingDatabases}
          loading={loading}
        />
      </main>
    </div>
  );
}
